import { useEffect, useState } from 'react';

import { api } from './api';
import { CalendarIcon } from './icons';

interface ClockAnchor {
  serverTimeMs: number;
  receivedAtMs: number;
}

const dateTimeFormatter = new Intl.DateTimeFormat('vi-VN', {
  timeZone: 'Asia/Ho_Chi_Minh',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});
const weekdayFormatter = new Intl.DateTimeFormat('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', weekday: 'long' });

const digitSegments: Record<string, string> = {
  '0': 'abcdef',
  '1': 'bc',
  '2': 'abged',
  '3': 'abgcd',
  '4': 'fgbc',
  '5': 'afgcd',
  '6': 'afgecd',
  '7': 'abc',
  '8': 'abcdefg',
  '9': 'abfgcd'
};

const segmentShapes = [
  ['a', '4,1 16,1 13.5,3.5 6.5,3.5'],
  ['b', '17,2 19,4 19,14 16.5,16 15.5,14 15.5,6'],
  ['c', '16.5,16 19,18 19,28 17,30 15.5,26 15.5,18'],
  ['d', '4,31 16,31 13.5,28.5 6.5,28.5'],
  ['e', '1,18 3.5,16 4.5,18 4.5,26 3,30 1,28'],
  ['f', '1,4 3,2 4.5,6 4.5,14 3.5,16 1,14'],
  ['g', '4,16 6.5,14.5 13.5,14.5 16,16 13.5,17.5 6.5,17.5']
] as const;

export function getServerDateTimeParts(date: Date): { date: string; time: string } {
  const parts = Object.fromEntries(dateTimeFormatter.formatToParts(date).map(({ type, value }) => [type, value]));
  return {
    date: `${weekdayFormatter.format(date)}, ${parts.day}/${parts.month}/${parts.year}`,
    time: `${parts.hour}:${parts.minute}:${parts.second}`
  };
}

export function formatServerDateTime(date: Date): string {
  const parts = getServerDateTimeParts(date);
  return `${parts.date} · ${parts.time}`;
}

function DigitalTime({ value }: { value: string }) {
  return (
    <span className="digital-time" aria-hidden="true">
      {Array.from(value).map((character, index) => character === ':' ? (
        <svg className="digital-colon" viewBox="0 0 5 32" key={index}><circle cx="2.5" cy="10" r="1.7" /><circle cx="2.5" cy="23" r="1.7" /></svg>
      ) : (
        <svg className="digital-digit" viewBox="0 0 20 32" key={index}>
          {segmentShapes.map(([segment, points]) => <polygon key={segment} points={points} className={digitSegments[character]?.includes(segment) ? 'lit' : ''} />)}
        </svg>
      ))}
    </span>
  );
}

export function ServerClock() {
  const [anchor, setAnchor] = useState<ClockAnchor | null>(null);
  const [tick, setTick] = useState(() => performance.now());
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let controller: AbortController | null = null;

    async function sync() {
      controller?.abort();
      const currentController = new AbortController();
      controller = currentController;
      const startedAtMs = performance.now();
      try {
        const result = await api<{ serverTime: string }>('/api/system/time', {}, currentController.signal);
        const receivedAtMs = performance.now();
        const serverTimeMs = Date.parse(result.serverTime);
        if (!Number.isFinite(serverTimeMs)) throw new Error('Giờ máy chủ không hợp lệ.');
        if (!active) return;
        setAnchor({ serverTimeMs: serverTimeMs + (receivedAtMs - startedAtMs) / 2, receivedAtMs });
        setTick(receivedAtMs);
        setError(false);
      } catch {
        if (active && !currentController.signal.aborted) setError(true);
      }
    }

    void sync();
    const tickTimer = window.setInterval(() => setTick(performance.now()), 1000);
    const syncTimer = window.setInterval(() => void sync(), 60_000);
    return () => {
      active = false;
      controller?.abort();
      window.clearInterval(tickTimer);
      window.clearInterval(syncTimer);
    };
  }, []);

  const currentTime = anchor ? new Date(anchor.serverTimeMs + tick - anchor.receivedAtMs) : null;
  const display = currentTime ? getServerDateTimeParts(currentTime) : null;
  const readableTime = currentTime ? formatServerDateTime(currentTime) : null;

  return (
    <time className="topbar-title topbar-clock" dateTime={currentTime?.toISOString()} title={error ? 'Không thể đồng bộ lại giờ máy chủ' : 'Giờ máy chủ · đồng bộ mỗi phút'} aria-label={readableTime ? `Giờ máy chủ: ${readableTime}` : 'Đang đồng bộ giờ máy chủ'}>
      <CalendarIcon />
      {display ? <><span className="topbar-clock-date">{display.date}</span><DigitalTime value={display.time} /></> : error ? 'Không lấy được giờ máy chủ' : 'Đang đồng bộ giờ máy chủ…'}
    </time>
  );
}
